import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ArrowRight, Package, PencilRuler, Boxes, Tag,
  AlertTriangle, Trash2, CheckCircle2, Paperclip, X, Mail, MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { pimg, SITE, formatCategoryName } from "../lib/site";
import { useInquiry } from "../context/InquiryContext";
import { useSettings } from "../context/SettingsContext";
import { EASE } from "../components/motion/Reveal";
import PageMeta from "../components/PageMeta";

const TYPES = [
  { id: "product", icon: Package, label: "Product", desc: "Order from the catalog" },
  { id: "custom", icon: PencilRuler, label: "Custom Product", desc: "Built to your spec" },
  { id: "bulk", icon: Boxes, label: "Bulk Order", desc: "Wholesale volumes" },
  { id: "private-label", icon: Tag, label: "Private Label", desc: "Your brand, our factory" },
];

const STEP_LABELS = ["Offering", "Product", "Requirements", "Details", "Review"];
const CUSTOMIZATION_OPTIONS = ["Logo printing", "Custom packaging", "Private label", "Custom colors"];

const stepAnim = {
  initial: { opacity: 0, x: 48 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -48 },
  transition: { duration: 0.45, ease: EASE },
};

const Inquiry = () => {
  const [params] = useSearchParams();
  const preProduct = params.get("product") || "";
  const preType = params.get("type") || "";
  const { items: cartItems, removeItem, updateQuantity, clear } = useInquiry();
  const site = useSettings();

  const [products, setProducts] = useState([]);
  const [step, setStep] = useState(preProduct ? 3 : preType ? 2 : 1);
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState("");
  const [form, setForm] = useState({
    type: preType || (preProduct ? "product" : ""),
    category: "",
    productId: preProduct,
    quantity: "",
    size: "",
    color: "",
    material: "",
    customization: [],
    instructions: "",
    name: "",
    company: "",
    email: "",
    phone: "",
    country: "",
    files: [],
  });

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("files", [...form.files, { path: r.data.path, filename: r.data.filename }]);
      toast.success(`${f.name} attached`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  const product = useMemo(() => products.find((p) => p.id === form.productId), [products, form.productId]);
  const categories = useMemo(() => [...new Set(products.map((p) => formatCategoryName(p.category)).filter(Boolean))], [products]);
  const categoryProducts = form.category ? products.filter((p) => formatCategoryName(p.category) === form.category || p.category === form.category) : products;

  useEffect(() => {
    if (product && !form.quantity) set("quantity", product.moq);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  const qty = Number(form.quantity) || 0;
  const belowMoq = product && qty > 0 && qty < product.moq;
  const tierPrice = (p, q) =>
    p?.price_tiers?.length
      ? p.price_tiers.reduce((best, t) => (q >= t.min_qty ? t.price : best), p.price_tiers[0].price)
      : null;
  const currentTierPrice = product && qty > 0 ? tierPrice(product, qty) : null;
  const nextTier = product?.price_tiers?.find((t) => t.min_qty > qty) || null;

  const canNext = () => {
    if (step === 1) return !!form.type;
    if (step === 2) return form.type === "product" ? !!form.productId : true;
    if (step === 3) return qty > 0 || !product;
    if (step === 4)
      return form.name.trim() && /\S+@\S+\.\S+/.test(form.email) && form.phone.trim() && form.country.trim();
    return true;
  };

  const next = () => {
    if (!canNext()) {
      toast.error("Please complete the required fields");
      return;
    }
    setStep((s) => Math.min(5, s + 1));
    window.scrollTo(0, 0);
  };

  const submit = async () => {
    setSubmitting(true);
    const items = [];
    if (product) {
      items.push({
        product_id: product.id,
        product_name: product.name,
        quantity: qty || product.moq,
        size: form.size,
        color: form.color,
        material: form.material,
        customization: form.customization.join(", "),
        unit_price: tierPrice(product, qty || product.moq),
      });
    }
    cartItems.forEach((i) => {
      if (i.product_id !== form.productId) items.push(i);
    });
    try {
      const r = await api.post("/inquiries", {
        inquiry_type: form.type || "product",
        items,
        customer: {
          name: form.name,
          company: form.company,
          email: form.email,
          phone: form.phone,
          country: form.country,
        },
        requirements: { customization: form.customization, instructions: form.instructions },
        notes: form.instructions,
        files: form.files,
      });
      setReference(r.data.reference);
      clear();
      setStep(6);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1100px] px-5 py-16 md:py-24 lg:px-10" data-testid="inquiry-page">
      <PageMeta
        title="Request a Quote"
        description="Submit a B2B inquiry for custom OEM/ODM pickleball equipment. Get a detailed quotation within 24 hours."
      />
      <p className="mono-caption text-neutral-500">Inquiry Builder</p>
      <h1 className="mt-4 font-display text-4xl font-semibold tracking-tighter md:text-5xl">
        REQUEST A QUOTE<span className="text-brand-red">.</span>
      </h1>

      {step <= 5 && (
        <div className="mt-10 flex flex-wrap gap-x-8 gap-y-2 border-y border-black/10 py-4" data-testid="inquiry-progress">
          {STEP_LABELS.map((l, i) => (
            <span
              key={l}
              className={`font-mono text-[11px] uppercase tracking-[0.2em] ${
                step === i + 1 ? "text-brand-red" : step > i + 1 ? "text-brand-ink" : "text-neutral-300"
              }`}
            >
              0{i + 1} {l}
            </span>
          ))}
        </div>
      )}

      <div className="mt-12 min-h-[380px]">
        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div key="s1" {...stepAnim}>
              <h2 className="font-display text-2xl font-medium tracking-tight">What are you looking for?</h2>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {TYPES.map((t) => (
                  <button
                    key={t.id}
                    data-testid={`inquiry-type-option-${t.id}`}
                    onClick={() => set("type", t.id)}
                    className={`flex items-start gap-4 border p-6 text-left transition-colors ${
                      form.type === t.id ? "border-brand-red bg-brand-red/5" : "border-black/15 hover:border-brand-ink"
                    }`}
                  >
                    <t.icon size={24} className="mt-1 text-brand-red" />
                    <span>
                      <span className="block font-display text-xl font-medium">{t.label}</span>
                      <span className="mt-1 block text-sm text-neutral-500">{t.desc}</span>
                    </span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="s2" {...stepAnim}>
              <h2 className="font-display text-2xl font-medium tracking-tight">Select Product</h2>
              <div className="mt-6 flex flex-wrap gap-2">
                {categories.map((c) => (
                  <button
                    key={c}
                    data-testid={`inquiry-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => set("category", c)}
                    className={`border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${
                      form.category === c ? "border-brand-ink bg-brand-ink text-white" : "border-black/15 text-neutral-500 hover:border-brand-ink"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <div className="mt-6 grid gap-px border border-black/10 bg-black/10 sm:grid-cols-2">
                {categoryProducts.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`inquiry-product-${p.id}`}
                    onClick={() => set("productId", p.id)}
                    className={`flex items-center justify-between bg-white p-5 text-left transition-colors ${
                      form.productId === p.id ? "!bg-brand-ink text-white" : "hover:bg-brand-smoke"
                    }`}
                  >
                    <span>
                      <span className="block font-display text-lg font-medium">{p.name}</span>
                      <span className={`font-mono text-[11px] uppercase tracking-wider ${form.productId === p.id ? "text-brand-red" : "text-neutral-400"}`}>
                        MOQ {p.moq} {p.moq_unit || "pieces"}
                      </span>
                    </span>
                    {form.productId === p.id && <CheckCircle2 size={20} className="text-brand-red" />}
                  </button>
                ))}
              </div>
              {form.type !== "product" && (
                <p className="mt-4 text-sm text-neutral-400">Optional — skip if you'd like our team to recommend.</p>
              )}
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="s3" {...stepAnim}>
              <h2 className="font-display text-2xl font-medium tracking-tight">Requirements</h2>
              {product && (
                <div className="mt-5 flex items-center gap-4 border border-black/10 bg-brand-smoke p-4" data-testid="inquiry-selected-product">
                  <img src={pimg(product.images[0], 140, 140)} alt={product.name} className="h-14 w-14 shrink-0 object-cover" />
                  <div className="min-w-0">
                    <p className="truncate font-display text-lg font-medium tracking-tight">{product.name}</p>
                    <p className="font-mono text-[11px] uppercase tracking-wider text-neutral-400">
                      {formatCategoryName(product.category)} — MOQ {product.moq} {product.moq_unit || "pieces"}
                    </p>
                  </div>
                  <div className="ml-auto flex shrink-0 gap-2">
                    <Link to={`/products/${product.id}`} data-testid="inquiry-product-link" className="btn-outline !h-9 !px-3 !text-[10px]">
                      View Product
                    </Link>
                    <button type="button" onClick={() => setStep(2)} data-testid="inquiry-change-product" className="btn-outline !h-9 !px-3 !text-[10px]">
                      Change
                    </button>
                  </div>
                </div>
              )}
              <div className="mt-8 grid gap-6 sm:grid-cols-2">
                <div>
                  <label className="label-pp" htmlFor="inq-qty">Quantity *</label>
                  <input id="inq-qty" type="number" min="1" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} className="input-pp font-mono" data-testid="inquiry-quantity" placeholder="e.g. 500" />
                  {belowMoq && (
                    <div className="mt-3 flex items-start gap-3 border border-brand-red bg-brand-red/5 p-3" data-testid="inquiry-moq-warning">
                      <AlertTriangle size={16} className="mt-0.5 shrink-0 text-brand-red" />
                      <p className="font-mono text-xs text-brand-red">
                        Minimum order quantity for this product is {product.moq} {product.moq_unit || "pieces"}.
                      </p>
                    </div>
                  )}
                  {currentTierPrice != null && (
                    <p className="mt-2 font-mono text-xs text-neutral-500" data-testid="inquiry-tier-price">
                      Indicative unit price at {qty} {product.moq_unit || "pieces"}:{" "}
                      <span className="font-bold text-brand-ink">US ${currentTierPrice.toFixed(2)}</span>
                      {nextTier && (
                        <span className="text-brand-red"> · US ${nextTier.price.toFixed(2)} at {nextTier.min_qty}+</span>
                      )}
                    </p>
                  )}
                </div>
                <div>
                  <label className="label-pp" htmlFor="inq-material">Material</label>
                  <input id="inq-material" value={form.material} onChange={(e) => set("material", e.target.value)} className="input-pp" data-testid="inquiry-material" placeholder={product ? product.material : "e.g. T700 carbon"} />
                </div>
                <div>
                  <label className="label-pp" htmlFor="inq-size">Size</label>
                  <select id="inq-size" value={form.size} onChange={(e) => set("size", e.target.value)} className="input-pp" data-testid="inquiry-size">
                    <option value="">Select size</option>
                    {(product?.sizes || ["Standard", "Custom"]).map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <label className="label-pp" htmlFor="inq-color">Color</label>
                    <a
                      href="/PANTONE.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-[10px] text-brand-red underline hover:text-brand-reddark transition-colors inline-flex items-center gap-0.5"
                      data-testid="inquiry-pantone-link"
                    >
                      Pantone Guide ↗
                    </a>
                  </div>
                  <select id="inq-color" value={form.color} onChange={(e) => set("color", e.target.value)} className="input-pp" data-testid="inquiry-color">
                    <option value="">Select color</option>
                    {(product?.colors || ["Black", "White", "Custom Pantone"]).map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <p className="label-pp mt-8">Customization</p>
              <div className="flex flex-wrap gap-2" data-testid="inquiry-customization">
                {CUSTOMIZATION_OPTIONS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    data-testid={`inquiry-cust-${c.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() =>
                      set("customization", form.customization.includes(c)
                        ? form.customization.filter((x) => x !== c)
                        : [...form.customization, c])
                    }
                    className={`border px-4 py-2 text-xs transition-colors ${
                      form.customization.includes(c) ? "border-brand-red bg-brand-red/5 text-brand-red" : "border-black/15 text-neutral-500 hover:border-brand-ink"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <div className="mt-6">
                <label className="label-pp" htmlFor="inq-notes">Special Instructions</label>
                <textarea id="inq-notes" rows={4} value={form.instructions} onChange={(e) => set("instructions", e.target.value)} className="input-pp !h-auto py-3" data-testid="inquiry-notes" placeholder="Logo placement, packaging, target price, deadlines..." />
              </div>
              <div className="mt-6">
                <label className="label-pp">Attach Logo / Design / Reference (optional)</label>
                <label className="flex cursor-pointer items-center gap-3 border border-dashed border-brand-ink/40 p-5 text-sm text-neutral-500 transition-colors hover:border-brand-red hover:text-brand-ink" data-testid="inquiry-upload-dropzone">
                  <Paperclip size={18} className="shrink-0 text-brand-red" />
                  {uploading ? "Uploading..." : "Click to attach JPG, PNG, WEBP or PDF (max 10MB)"}
                  <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" onChange={handleFile} data-testid="inquiry-upload-input" />
                </label>
                {form.files.length > 0 && (
                  <div className="mt-3 space-y-2" data-testid="inquiry-attachments">
                    {form.files.map((f, i) => (
                      <div key={f.path} className="flex items-center justify-between border border-black/10 px-4 py-2.5 font-mono text-xs">
                        <span className="flex items-center gap-2 truncate">
                          <Paperclip size={12} className="shrink-0 text-brand-red" /> {f.filename}
                        </span>
                        <button type="button" onClick={() => set("files", form.files.filter((x) => x.path !== f.path))} data-testid={`inquiry-attachment-remove-${i}`} aria-label="Remove attachment" className="text-neutral-400 hover:text-brand-red">
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {step === 4 && (
            <motion.div key="s4" {...stepAnim}>
              <h2 className="font-display text-2xl font-medium tracking-tight">Your Details</h2>
              <div className="mt-8 grid gap-6 sm:grid-cols-2">
                <div>
                  <label className="label-pp" htmlFor="inq-name">Full Name *</label>
                  <input id="inq-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="input-pp" data-testid="inquiry-name" />
                </div>
                <div>
                  <label className="label-pp" htmlFor="inq-company">Company Name</label>
                  <input id="inq-company" value={form.company} onChange={(e) => set("company", e.target.value)} className="input-pp" data-testid="inquiry-company" />
                </div>
                <div>
                  <label className="label-pp" htmlFor="inq-email">Email *</label>
                  <input id="inq-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="input-pp" data-testid="inquiry-email" />
                </div>
                <div>
                  <label className="label-pp" htmlFor="inq-phone">Phone / WhatsApp *</label>
                  <input id="inq-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="input-pp" data-testid="inquiry-phone" />
                </div>
                <div className="sm:col-span-2">
                  <label className="label-pp" htmlFor="inq-country">Country *</label>
                  <input id="inq-country" value={form.country} onChange={(e) => set("country", e.target.value)} className="input-pp" data-testid="inquiry-country" />
                </div>
              </div>
            </motion.div>
          )}

          {step === 5 && (
            <motion.div key="s5" {...stepAnim}>
              <h2 className="font-display text-2xl font-medium tracking-tight">Review Your Inquiry</h2>
              <div className="mt-8 divide-y divide-black/10 border border-black/10" data-testid="inquiry-review">
                <div className="grid gap-1 p-5 sm:grid-cols-3">
                  <span className="mono-caption text-neutral-400">Type</span>
                  <span className="font-medium sm:col-span-2">{TYPES.find((t) => t.id === form.type)?.label || "Product"}</span>
                </div>
                {product && (
                  <div className="grid gap-1 p-5 sm:grid-cols-3">
                    <span className="mono-caption text-neutral-400">Product</span>
                    <span className="sm:col-span-2">
                      <span className="block font-medium">{product.name}</span>
                      <span className="font-mono text-xs text-neutral-500">
                        Qty {qty || product.moq} {form.size && `— ${form.size}`} {form.color && `— ${form.color}`}
                        {tierPrice(product, qty || product.moq) != null &&
                          ` — US $${tierPrice(product, qty || product.moq).toFixed(2)}/pc indicative`}
                      </span>
                    </span>
                  </div>
                )}
                {cartItems.filter((i) => i.product_id !== form.productId).map((i) => (
                  <div key={i.product_id} className="grid items-center gap-1 p-5 sm:grid-cols-3">
                    <span className="mono-caption text-neutral-400">Also Inquiring</span>
                    <span className="font-medium">{i.product_name}</span>
                    <span className="flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        value={i.quantity}
                        onChange={(e) => updateQuantity(i.product_id, Number(e.target.value) || 1)}
                        className="h-9 w-24 border border-black/20 text-center font-mono text-sm outline-none"
                        data-testid={`review-qty-${i.product_id}`}
                      />
                      <button onClick={() => removeItem(i.product_id)} aria-label="Remove item" data-testid={`review-remove-${i.product_id}`} className="text-neutral-400 hover:text-brand-red">
                        <Trash2 size={16} />
                      </button>
                    </span>
                  </div>
                ))}
                {form.customization.length > 0 && (
                  <div className="grid gap-1 p-5 sm:grid-cols-3">
                    <span className="mono-caption text-neutral-400">Customization</span>
                    <span className="sm:col-span-2">{form.customization.join(", ")}</span>
                  </div>
                )}
                {form.files.length > 0 && (
                  <div className="grid gap-1 p-5 sm:grid-cols-3">
                    <span className="mono-caption text-neutral-400">Attachments</span>
                    <span className="flex flex-wrap gap-2 sm:col-span-2" data-testid="review-attachments">
                      {form.files.map((f) => (
                        <span key={f.path} className="inline-flex items-center gap-2 border border-black/15 px-3 py-1 font-mono text-xs">
                          <Paperclip size={11} className="text-brand-red" /> {f.filename}
                        </span>
                      ))}
                    </span>
                  </div>
                )}
                <div className="grid gap-1 p-5 sm:grid-cols-3">
                  <span className="mono-caption text-neutral-400">Contact</span>
                  <span className="sm:col-span-2">
                    {form.name}{form.company && ` — ${form.company}`}<br />
                    <span className="text-sm text-neutral-500">{form.email} / {form.phone} / {form.country}</span>
                  </span>
                </div>
              </div>
            </motion.div>
          )}

          {step === 6 && (
            <motion.div key="s6" {...stepAnim} className="py-16 text-center" data-testid="inquiry-success">
              <CheckCircle2 size={56} className="mx-auto text-brand-red" />
              <h2 className="mt-6 font-display text-3xl font-semibold tracking-tight md:text-4xl">
                Inquiry Submitted<span className="text-brand-red">.</span>
              </h2>
              <p className="mt-4 text-neutral-500">
                Reference <span className="font-mono font-bold text-brand-ink" data-testid="inquiry-reference">{reference}</span>
              </p>
              <p className="mx-auto mt-2 max-w-md text-neutral-500">
                Our export team will respond with a detailed quotation within 24 hours.
              </p>

              {/* Email + WhatsApp quick-contact buttons */}
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <a
                  href={`mailto:${site.email}?subject=RE:%20Inquiry%20${reference}&body=Hi%20Pickleballgearz%20team%2C%20I%20am%20following%20up%20on%20my%20inquiry%20${reference}.`}
                  className="btn-outline"
                  data-testid="success-email"
                >
                  <Mail size={15} /> Email Us
                </a>
                <a
                  href={`https://wa.me/${site.phone.replace(/[^\d]/g, "")}?text=Hi%20Pickleballgearz%2C%20I%27m%20following%20up%20on%20my%20inquiry%20${encodeURIComponent(reference)}.%20Please%20let%20me%20know%20the%20status.`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-green"
                  data-testid="success-whatsapp"
                >
                  <MessageCircle size={15} /> WhatsApp
                </a>
              </div>

              <div className="mt-10 flex flex-wrap justify-center gap-4">
                <Link to="/products" className="btn-dark" data-testid="success-browse">Browse Products</Link>
                <Link to="/" className="btn-outline" data-testid="success-home">Back Home</Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {step <= 5 && (
        <div className="mt-12 flex items-center justify-between border-t border-black/10 pt-8">
          <button
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            disabled={step === 1}
            data-testid="inquiry-back-btn"
            className="btn-outline disabled:cursor-not-allowed disabled:opacity-30"
          >
            <ArrowLeft size={16} /> Back
          </button>
          {step < 5 ? (
            <button onClick={next} data-testid="inquiry-next-btn" className="btn-primary">
              Continue <ArrowRight size={16} />
            </button>
          ) : (
            <button onClick={submit} disabled={submitting} data-testid="inquiry-submit-btn" className="btn-primary disabled:opacity-50">
              {submitting ? "Submitting..." : "Submit Inquiry"} <ArrowRight size={16} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Inquiry;
